import Agent from "../api";
import { User, UserEntity, Result } from "core/models";

export interface Credentials {
    id: number;
    userId: number;
    token: string;
    insertionDate: Date;
    expiryDate: Date;
}

interface CredentialsEntity {
    session: Credentials;
    refreshSession: Credentials;
    user: UserEntity;
}

export class Session {
    token: string;

    id: number;

    userID: number;

    insertionDate: Date;

    expiryDate: Date;

    refreshCredentials: Credentials;

    user: User;

    constructor(data: CredentialsEntity) {
        this.token = data.session.token;
        this.id = data.session.id;
        this.userID = data.session.userId;
        this.insertionDate = data.session.insertionDate;
        this.expiryDate = data.session.expiryDate;
        this.refreshCredentials = data.refreshSession;
        this.user = new User(data.user);
    }
}

export interface UpdateUserFields {
    currentPassword?: string;
    password?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    organization?: string;
}

const authenticateUser = async (
    username: string,
    password: string,
): Promise<Result<Session, string>> => {
    const request = {
        username,
        password,
    };
    const response = await Agent.post<CredentialsEntity>(
        "/user/login/v1",
        request,
    );

    // Currently we don't parse the messages, but we could
    // to return better error messages
    if (!response.resultObject) {
        return {
            value: undefined,
            error: "Invalid username/password",
        };
    }

    return {
        value: new Session(response.resultObject),
        error: undefined,
    };
};

/**
 * Attempts to renew an old session with the refreshToken of that session
 * @param {string} refreshToken Refresh token to create a new session with
 */
const renewAccessToken = async (refreshToken: string): Promise<Session> => {
    const request = {
        // serviceName can be kept empty for simple user refresh tokens
        serviceName: "",
        refreshToken,
    };
    const response = await Agent.post<CredentialsEntity>(
        "/user/renewtoken/v1",
        request,
    );
    if (response.resultObject) {
        return new Session(response.resultObject);
    }
    throw new Error("Error while renewing accesstoken");
};

const updateUser = async (
    userID: number,
    username: string,
    password: string,
): Promise<Result<Session, string>> => {
    const request = {
        id: userID,
        username,
        password,
    };
    const response = await Agent.post<CredentialsEntity>(
        "/user/update/v1",
        request,
    );

    if (!response.resultObject) {
        return {
            value: undefined,
            error: "unable to update user",
        };
    }

    return {
        value: new Session(response.resultObject),
        error: undefined,
    };
};

const getLoggedIn = async (
    accessToken: string,
): Promise<Result<User, string>> => {
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const request = {};
    const response = await Agent.post<UserEntity>(
        "user/getloggedin/v1",
        request,
        headers,
    );

    if (!response.resultObject) {
        return {
            value: undefined,
            error: "unable get loggedIn user info",
        };
    }
    return {
        value: new User(response.resultObject),
        error: undefined,
    };
};

const updateLoggedIn = async (
    accessToken: string,
    updateObject: UpdateUserFields,
): Promise<Result<User, string>> => {
    const updateFieldNames = Object.keys(updateObject);
    if (updateFieldNames.length === 0) {
        throw new Error("updateObject was empty");
    }

    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const request = {
        ...updateObject,
        updateFieldNames,
    };
    const response = await Agent.post<User>(
        "user/updateloggedin/v1",
        request,
        headers,
    );

    if (!response.resultObject) {
        if (response.messages[0].message) {
            return {
                value: undefined,
                error: response.messages[0].message,
            };
        }
        return {
            value: undefined,
            error: "undefined error while updating loggedinUser",
        };
    }
    return {
        value: response.resultObject,
        error: undefined,
    };
};

const logoutUser = async (accessToken: string): Promise<void> => {
    const request = {
        endAllSessions: false,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    await Agent.post<Record<string, unknown>>(
        "/user/logout/v1",
        request,
        headers,
    );
};

export {
    authenticateUser,
    updateUser,
    logoutUser,
    renewAccessToken,
    getLoggedIn,
    updateLoggedIn,
};
